import { KPI, KPIHealth, KPIHistoryPoint, KPIPeriodicity, Task, Objective, OKRState } from './types';

// ── KPI calculations (mirror of backend calculations.ts) ─────────────────────

export function computeKPIProgress(kpi: KPI): number {
    const { currentValue: c, targetValue: t, direction: d } = kpi;
    if (t === 0) return c === 0 ? 100 : 0;
    if (d === 'lower-is-better') {
        return Math.min(100, Math.max(0, Math.round((100 - ((c - t) / Math.abs(t)) * 100) * 10) / 10));
    }
    return Math.min(100, Math.max(0, Math.round((c / t) * 100 * 10) / 10));
}

export function computeKPIHealth(kpi: KPI): KPIHealth {
    const { currentValue: c, targetValue: t, minAcceptableValue: m, direction: d } = kpi;
    if (d === 'higher-is-better') {
        if (c >= t) return 'good';
        if (c >= m) return 'warning';
        return 'critical';
    }
    if (c <= t) return 'good';
    if (c <= m) return 'warning';
    return 'critical';
}

export function computeObjectiveProgress(obj: Objective, kpis: KPI[]): number {
    if (obj.progress_mode === 'manual') return Math.min(100, Math.max(0, obj.manual_progress ?? 0));
    const objKpis = kpis.filter((k) => obj.kpiIds.includes(k.id) || obj.kpiIds.includes(k._id ?? ''));
    if (objKpis.length === 0) return 0;
    return Math.round(objKpis.reduce((s, k) => s + computeKPIProgress(k), 0) / objKpis.length);
}

export function computeSmartScore(task: Task): number {
    if (task.effort <= 0) return 0;
    if (task.smart_score !== undefined) return task.smart_score;
    return Math.round(((task.impactKPI * task.urgency) / task.effort) * 10) / 10;
}

// ── KPI periodicity / history table (mirror of backend calculations.ts) ──────

export const periodicityLabels: Record<KPIPeriodicity, string> = {
    daily: 'Quotidien', weekly: 'Hebdomadaire', monthly: 'Mensuel', quarterly: 'Trimestriel',
};

function isoWeekLabel(d: Date): string {
    const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = date.getUTCDay() || 7;
    date.setUTCDate(date.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    return `${date.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

/** Date label for a given period, matching the backend's appendHistory granularity. */
export function periodDateLabel(periodicity: KPIPeriodicity, date: Date = new Date()): string {
    if (periodicity === 'daily') return date.toISOString().slice(0, 10);
    if (periodicity === 'weekly') return isoWeekLabel(date);
    if (periodicity === 'quarterly') return `${date.getFullYear()}-Q${Math.floor(date.getMonth() / 3) + 1}`;
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/** Next period's date, stepped from `from` according to the periodicity. */
export function nextPeriodDate(periodicity: KPIPeriodicity, from: Date = new Date()): Date {
    const d = new Date(from);
    if (periodicity === 'daily') d.setDate(d.getDate() + 1);
    else if (periodicity === 'weekly') d.setDate(d.getDate() + 7);
    else if (periodicity === 'quarterly') d.setMonth(d.getMonth() + 3);
    else d.setMonth(d.getMonth() + 1);
    return d;
}

/** Parses a stored date-label ("2026-07", "2026-W29", "2026-07-19", "2026-Q3") back into a Date. */
export function parsePeriodLabel(label: string, periodicity: KPIPeriodicity): Date {
    if (periodicity === 'weekly') {
        const match = /^(\d{4})-W(\d{2})$/.exec(label);
        if (match) {
            const year = Number(match[1]);
            const week = Number(match[2]);
            const jan4 = new Date(Date.UTC(year, 0, 4));
            const jan4Day = jan4.getUTCDay() || 7;
            const weekStart = new Date(jan4);
            weekStart.setUTCDate(jan4.getUTCDate() - jan4Day + 1 + (week - 1) * 7);
            return weekStart;
        }
    }
    if (periodicity === 'quarterly') {
        const match = /^(\d{4})-Q(\d)$/.exec(label);
        if (match) return new Date(Number(match[1]), (Number(match[2]) - 1) * 3, 1);
    }
    if (periodicity === 'monthly' && /^\d{4}-\d{2}$/.test(label)) return new Date(`${label}-01`);
    return new Date(label);
}

/** The next unlogged period's date-label, given the last logged period. */
export function nextPeriodLabel(periodicity: KPIPeriodicity, lastLabel: string | null): string {
    if (!lastLabel) return periodDateLabel(periodicity);
    return periodDateLabel(periodicity, nextPeriodDate(periodicity, parsePeriodLabel(lastLabel, periodicity)));
}

/** Human-readable label for a stored date-label ("2026-07", "2026-W29", "2026-07-19"). */
export function formatPeriodLabel(label: string, periodicity: KPIPeriodicity): string {
    if (periodicity === 'monthly' && /^\d{4}-\d{2}$/.test(label)) {
        const [y, m] = label.split('-').map(Number);
        return new Date(y, m - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    }
    if (periodicity === 'weekly' && /^\d{4}-W\d{2}$/.test(label)) {
        const [y, w] = label.split('-W');
        return `Sem. ${w} — ${y}`;
    }
    if (periodicity === 'daily' && /^\d{4}-\d{2}-\d{2}$/.test(label)) {
        return new Date(`${label}T00:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
    }
    if (periodicity === 'quarterly' && /^\d{4}-Q\d$/.test(label)) {
        const [y, q] = label.split('-Q');
        return `T${q} — ${y}`;
    }
    return label;
}

export interface HistoryRowComputed extends KPIHistoryPoint {
    change: number | null;
    variationPct: number | null;
}

/** Computes, for each history point, the change and % variation vs. the previous period. */
export function computeHistoryDeltas(history: KPIHistoryPoint[]): HistoryRowComputed[] {
    return history.map((h, i) => {
        if (i === 0) return { ...h, change: null, variationPct: null };
        const prev = history[i - 1].value;
        const change = Math.round((h.value - prev) * 100) / 100;
        const variationPct = prev !== 0 ? Math.round((change / Math.abs(prev)) * 1000) / 10 : null;
        return { ...h, change, variationPct };
    });
}

// ── Team helpers ─────────────────────────────────────────────────────────────

export function getTeamMember(id: string, state: OKRState) {
    return state.team.find((m) => m.id === id);
}
export function getTeamMemberName(id: string, state: OKRState): string {
    return getTeamMember(id, state)?.name ?? 'Non assigné';
}
export function getTeamMemberInitials(id: string, state: OKRState): string {
    return getTeamMember(id, state)?.avatarInitials ?? '??';
}
export function getTeamMemberColor(id: string, state: OKRState): string {
    return getTeamMember(id, state)?.avatarColor ?? '#A3AED0';
}

// ── Formatting ───────────────────────────────────────────────────────────────

export function formatValue(value: number, unit: string): string {
    if (unit === '€') return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
    const f = value % 1 === 0 ? value.toString() : value.toFixed(1);
    return unit ? `${f} ${unit}` : f;
}

// ── Color maps ───────────────────────────────────────────────────────────────

export const healthColors: Record<KPIHealth, string> = {
    good: '#17ad37', warning: '#FFB547', critical: '#ea0606',
};

export const statusColors: Record<string, string> = {
    'on-track': 'text-green-600 bg-green-50',
    'at-risk': 'text-orange-600 bg-orange-50',
    'behind': 'text-red-600 bg-red-50',
    'completed': 'text-toola-500 bg-blue-50',
    'not-started': 'text-gray-500 bg-gray-100',
};

export const statusLabels: Record<string, string> = {
    'on-track': 'En avance', 'at-risk': 'À risque', 'behind': 'En retard',
    'completed': 'Terminé', 'not-started': 'Non démarré',
};

export const priorityColors: Record<string, string> = {
    critical: 'text-red-600 bg-red-50 border-red-200',
    high: 'text-orange-600 bg-orange-50 border-orange-200',
    medium: 'text-amber-600 bg-amber-50 border-amber-200',
    low: 'text-gray-500 bg-gray-50 border-gray-200',
};

export const priorityLabels: Record<string, string> = {
    critical: 'Critique', high: 'Haute', medium: 'Moyenne', low: 'Faible',
};

export const taskStatusConfig: Record<string, { label: string; color: string; bg: string }> = {
    'todo': { label: 'À faire', color: '#A3AED0', bg: '#F4F7FE' },
    'in-progress': { label: 'En cours', color: '#0260CB', bg: '#EBF3FF' },
    'waiting': { label: 'En attente', color: '#FFB547', bg: '#FFF8EB' },
    'blocked': { label: 'Bloquée', color: '#ea0606', bg: '#FFF0F0' },
    'done': { label: 'Terminée', color: '#17ad37', bg: '#F0FFF4' },
};

/** Convert API _id to local id for consistent key usage. */
export function normaliseId<T extends { id?: string; _id?: string }>(obj: T): T & { id: string } {
    return { ...obj, id: obj.id ?? obj._id ?? '' };
}

// ── Recurring task generation (client-side, for offline / demo mode) ──────────

const RECURRENCE_MAX: Record<string, number> = { daily: 90, weekly: 52, monthly: 12 };

function frDate(d: Date): string {
    const months = ['jan', 'fév', 'mar', 'avr', 'mai', 'jun', 'jul', 'aoû', 'sep', 'oct', 'nov', 'déc'];
    return `${String(d.getDate()).padStart(2, '0')} ${months[d.getMonth()]}`;
}

export function generateRecurringTasks(plan: import('./types').ActionPlan): import('./types').Task[] {
    const recurrence = plan.recurrence ?? 'none';
    const title = (plan.recurrenceTaskTitle ?? '').trim();
    if (recurrence === 'none' || !title) return [];

    const result: import('./types').Task[] = [];
    const current = new Date();
    const deadline = new Date(plan.deadline);
    const max = RECURRENCE_MAX[recurrence] ?? 52;

    while (current <= deadline && result.length < max) {
        result.push({
            id: `task-recur-${plan.id}-${result.length}`,
            actionPlanId: plan.id,
            objectiveId: plan.objectiveId,
            title: `${title} — ${frDate(new Date(current))}`,
            description: '',
            assigneeId: plan.responsibleUserId,
            dueDate: new Date(current).toISOString(),
            priority: 'medium' as const,
            estimatedHours: 0,
            spentHours: 0,
            status: 'todo' as const,
            impactKPI: 5, urgency: 5, effort: 5,
            smart_score: 5,
            tags: ['récurrent'],
        });
        if (recurrence === 'daily') current.setDate(current.getDate() + 1);
        else if (recurrence === 'weekly') current.setDate(current.getDate() + 7);
        else if (recurrence === 'monthly') current.setMonth(current.getMonth() + 1);
    }
    return result;
}

/**
 * Counts how many recurring tasks would be generated from today until deadline.
 * Uses the same stepping logic as generateRecurringTasks to ensure accuracy
 * (avoids the 30-day month approximation bug).
 */
export function countRecurringTasks(recurrence: string, deadline: string): number {
    if (recurrence === 'none') return 0;
    const max = RECURRENCE_MAX[recurrence] ?? 52;
    const end = new Date(deadline);
    const current = new Date();
    if (current > end) return 0;

    let count = 0;
    while (current <= end && count < max) {
        count++;
        if (recurrence === 'daily') current.setDate(current.getDate() + 1);
        else if (recurrence === 'weekly') current.setDate(current.getDate() + 7);
        else if (recurrence === 'monthly') current.setMonth(current.getMonth() + 1);
        else break;
    }
    return count;
}
